/**
 * Backend'in eşzamanlılık yanıtlarını (409 / 422 / 503) tek yerde yorumlar.
 *
 * Bu üç kod SCRUM-653 ile geldi; öncesinde hepsi 500 dönüyordu. Tek yerde
 * yorumlanmalarının sebebi, ekranda benzer görünüp DAVRANIŞLARI zıt olmaları:
 *
 *   409 → istek zaten alınmış ya da hâlâ işleniyor. Tekrar göndermek YENİ bir
 *         kayıt yaratma riski taşır; kullanıcı önce listeyi kontrol etmeli.
 *   422 → aynı idempotency anahtarı FARKLI bir gövdeyle geldi. Anahtar yanlış
 *         kayda bağlı kalmış demektir; tazelenmeden tekrar denemek anlamsız.
 *   503 → geçici kilit çakışması/deadlock. Aynı istek birkaç saniye sonra
 *         doğru sonucu verir; anahtar KORUNMALI ki retry çift kayıt yazmasın.
 *
 * Ayrıca hangi durumda idempotency anahtarının tazeleneceğine burada karar
 * verilir ({@link ApiErrorDetail.shouldResetKey}) — bu karar formların
 * hepsinde aynı olmak zorunda, kopyalanırsa er geç biri yanlış tarafa düşer.
 */

/** Sunucu Retry-After vermediyse kullanıcıya söylenecek makul bekleme. */
const DEFAULT_RETRY_AFTER_SECONDS = 3;

/** Sunucu mesajı boş geldiğinde kullanılacak yedek metinler. */
const FALLBACK_BY_STATUS = {
    409: "Bu işlem zaten kaydedilmiş görünüyor. Lütfen listeyi yenileyip kontrol edin.",
    422: "Bu form daha önce farklı bilgilerle gönderilmiş. Lütfen sayfayı yenileyip tekrar deneyin.",
    429: "Çok fazla istek gönderdiniz. Lütfen bir dakika sonra tekrar deneyin.",
    503: "Kayıt şu anda başka bir işlem tarafından güncelleniyor. Lütfen birkaç saniye sonra tekrar deneyin.",
};

/**
 * Anahtarın tazelenmesi GÜVENLİ olmayan durumlar.
 *
 * Ortak yanları: isteğin sunucuda ne olduğu kesin DEĞİL ya da hâlâ sürüyor.
 * Böyle bir durumda yeni anahtar üretmek, kullanıcının tekrar göndermesini
 * "yeni bir işlem" hâline getirir ve idempotency korumasını tam da işe
 * yarayacağı anda devre dışı bırakır.
 */
const KEEP_KEY_STATUSES = new Set([409, 429, 503]);

/**
 * @typedef {Object} ApiErrorDetail
 * @property {number|null} status          HTTP kodu; sunucuya hiç ulaşılamadıysa null.
 * @property {string} message              Kullanıcıya gösterilecek metin.
 * @property {boolean} serverResponded     Sunucu bir yanıt döndü mü (ağ hatası değil mi).
 * @property {boolean} retryable           Aynı istek tekrar denenebilir mi (503/429).
 * @property {number|null} retryAfterSeconds
 * @property {boolean} shouldResetKey      Idempotency anahtarı tazelenmeli mi.
 */

const buildDetail = (status, serverMessage, retryAfterHeader, fallbackMessage) => {
    const retryable = status === 503 || status === 429;
    const parsedRetryAfter = Number.parseInt(retryAfterHeader, 10);

    return {
        status: status ?? null,
        message:
            (typeof serverMessage === "string" && serverMessage.trim())
            || FALLBACK_BY_STATUS[status]
            || fallbackMessage,
        serverResponded: status != null,
        retryable,
        retryAfterSeconds: retryable
            ? (Number.isFinite(parsedRetryAfter) ? parsedRetryAfter : DEFAULT_RETRY_AFTER_SECONDS)
            : null,
        // Sunucu isteği görüp REDDETTİYSE kayıt oluşmamıştır: kullanıcı formu
        // düzeltip gönderdiğinde gövde değişeceği için eski anahtar 422 üretirdi.
        // Bu yüzden reddedilen isteklerde anahtar tazelenir; belirsiz veya
        // sürmekte olan durumlarda (KEEP_KEY_STATUSES, ağ hatası) korunur.
        shouldResetKey: status != null && !KEEP_KEY_STATUSES.has(status),
    };
};

/**
 * axios hatasını yorumlar (`catch (err)` bloklarında).
 * @returns {ApiErrorDetail}
 */
export const describeAxiosError = (err, fallbackMessage = "İşlem tamamlanamadı.") => {
    const response = err?.response;
    if (!response) {
        // Ağ hatası / timeout: istek sunucuya ULAŞMIŞ olabilir. Anahtar korunur.
        return buildDetail(null, null, null, "Sunucuya ulaşılamadı. Lütfen bağlantınızı kontrol edip tekrar deneyin.");
    }
    const data = response.data;
    const serverMessage = (data && typeof data === "object") ? data.message : null;
    return buildDetail(response.status, serverMessage, response.headers?.["retry-after"], fallbackMessage);
};

/**
 * fetch yanıtını yorumlar. fetch 4xx/5xx'te exception atmadığı için hata dalı
 * çağıranda kurulur; bu yüzden yanıt ve çözülmüş gövde ayrı ayrı geçilir.
 * @returns {ApiErrorDetail}
 */
export const describeFetchError = (response, json, fallbackMessage = "İşlem tamamlanamadı.") =>
    buildDetail(response?.status, json?.message, response?.headers?.get?.("Retry-After"), fallbackMessage);

